import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdr = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// Round 74 item 8: quarterly admin access review (records only). Id prefixes, roles and permission names - never an address.
export const AccessReviewCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});
  const load = async () => {
    setErr(null);
    try { setData((await axios.get(`${API_URL}/api/admin/access-reviews`, hdr(token))).data); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not load access reviews'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);
  const open = data?.open;
  const decide = async (adminId) => {
    const d = draft[adminId] || {};
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API_URL}/api/admin/access-reviews/${open.id}/decide`, { admin_id: adminId, decision: d.decision || 'keep', reason: d.reason || '' }, hdr(token));
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Decision refused'); } finally { setBusy(false); }
  };
  const complete = async () => {
    setBusy(true); setErr(null);
    try { await axios.post(`${API_URL}/api/admin/access-reviews/${open.id}/complete`, {}, hdr(token)); await load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not complete the review'); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-2xl border p-5 mb-6 ${open ? 'border-amber-300 bg-amber-50/40' : 'border-border/60'}`} data-testid="access-review-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Quarterly access review {open ? `— ${open.period} OPEN` : ''}</h3>
          <p className="text-xs text-muted-foreground">{data?.schedule || '1 Jan / 1 Apr / 1 Jul / 1 Oct'}. Records only: nothing changes automatically and the last active owner is never suspended.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="access-review-refresh-btn">Refresh</Button>
      </div>
      {err && <p className="text-sm text-red-700 mt-2" data-testid="access-review-error">{err}</p>}
      {data && !open && <p className="text-sm mt-3 text-muted-foreground" data-testid="access-review-none">No review is open for {data.period_now}. The next one opens on schedule (or via Diagnostics → Job Run Ledger → run-now on quarterly_access_review).</p>}
      {open && (
        <div className="mt-3 space-y-3" data-testid="access-review-open">
          <p className="text-xs">Due {open.due_at?.slice(0, 10)} · {open.admins.length} account(s) · {Object.keys(open.decisions || {}).length} decided</p>
          {open.admins.map((a) => {
            const dec = open.decisions?.[a.admin_id];
            const d = draft[a.admin_id] || {};
            return (
              <div key={a.admin_id} className="rounded-lg border border-border/60 bg-white p-3 text-sm" data-testid={`access-review-admin-${a.id_prefix}`}>
                <p className="font-mono text-xs">{a.id_prefix} · role {a.role} · status {a.status} · last sign-in {(a.last_sign_in_at || 'never').slice(0, 10)} · {a.requests_90d} requests / 90 d</p>
                <p className="text-xs mt-1">Permissions used {a.permissions_used_90d.length}/{a.permissions_granted.length}{a.permissions_unused_90d.length > 0 && <> · unused: <span className="font-mono">{a.permissions_unused_90d.join(', ')}</span></>}</p>
                {a.flags.map((f) => <p key={f} className="text-xs text-amber-800" data-testid={`access-review-flag-${a.id_prefix}`}>Flag: {f}</p>)}
                {dec ? (
                  <p className="text-xs mt-2 text-green-800" data-testid={`access-review-decided-${a.id_prefix}`}>Decided: <b>{dec.decision}</b> — {dec.reason} ({dec.decided_at?.slice(0, 16)}Z)</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <select className="border rounded px-2 py-1 text-xs" value={d.decision || 'keep'} onChange={(e) => setDraft({ ...draft, [a.admin_id]: { ...d, decision: e.target.value } })} data-testid={`access-review-decision-${a.id_prefix}`}>
                      {(data.decisions || ['keep', 'reduce', 'remove']).map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <input className="border rounded px-2 py-1 text-xs flex-1 min-w-[12rem]" placeholder={`Reason (at least ${data.min_reason || 8} characters)`} value={d.reason || ''} onChange={(e) => setDraft({ ...draft, [a.admin_id]: { ...d, reason: e.target.value } })} data-testid={`access-review-reason-${a.id_prefix}`} />
                    <Button size="sm" onClick={() => decide(a.admin_id)} disabled={busy} data-testid={`access-review-record-${a.id_prefix}`}>Record</Button>
                  </div>
                )}
              </div>
            );
          })}
          <Button size="sm" variant="destructive" onClick={complete} disabled={busy || Object.keys(open.decisions || {}).length < open.admins.length} data-testid="access-review-complete-btn">Complete review</Button>
        </div>
      )}
      {data?.history?.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-3" data-testid="access-review-history">Completed reviews: {data.history.map((h) => `${h.period} (${h.completed_at?.slice(0, 10)}; keep ${h.decision_counts?.keep ?? 0}, reduce ${h.decision_counts?.reduce ?? 0}, remove ${h.decision_counts?.remove ?? 0})`).join(' · ')}</p>
      )}
    </div>
  );
};
